import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';

const LocationSearch = ({ location = { lat: "", lng: "" }, setLocation = () => {} }) => {
  const [locationnew, setLocationnew] = useState(location);
  const [inputValue, setInputValue] = useState('');
  const [isManualInput, setIsManualInput] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Update local state when prop changes
  useEffect(() => {
    console.log("run in location search 0"); 
    if (location && location.lat && location.lng && 
        (location.lat !== locationnew.lat || location.lng !== locationnew.lng)) {
      setLocationnew(location);
    }
  }, [location, locationnew.lat, locationnew.lng]);

  // Initialize input event listeners
  useEffect(() => {
    if (!inputRef.current) return;

    // Prevent form submission on enter
    inputRef.current.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        });
      }
    };
  }, []);

  // Get current location and reverse geocode using Nominatim
  const getCurrentLocation = () => {
    console.log('getCurrentLocation called');
    setIsGettingLocation(true);
    setLocationError(null);

    if (navigator.geolocation) {
      console.log('Getting geolocation...');
      
      const options = {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds timeout
        maximumAge: 300000 // 5 minutes cache
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const newLocation = { lat, lng };
          console.log('Got current location:', newLocation);

          setLocation(newLocation);
          setLocationnew(newLocation);
          setIsManualInput(true);
          setShowSuggestions(false);
          setIsGettingLocation(false);
          setLocationError(null);
          localStorage.setItem('location-lat', lat);
          localStorage.setItem('location-lng', lng);

          // Reverse geocode using Nominatim
          reverseGeocode(newLocation);
        },
        (error) => {
          console.error('Geolocation failed:', error);
          setIsGettingLocation(false);
          handleGeolocationError(error);
        },
        options
      );
    } else {
      console.error('Geolocation not supported');
      setIsGettingLocation(false);
      setLocationError('Geolocation is not supported by this browser. Please enter your location manually.');
    }
  };

  // Handle geolocation errors with specific messages
  const handleGeolocationError = (error) => {
    let errorMessage = 'Unable to get your location. ';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage += 'Please allow location access and try again, or enter your location manually.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage += 'Location information is unavailable. Please enter your location manually.';
        break;
      case error.TIMEOUT:
        errorMessage += 'Location request timed out. Please try again or enter your location manually.';
        break;
      default:
        errorMessage += 'An unknown error occurred. Please enter your location manually.';
        break;
    }
    
    console.error('Geolocation error details:', errorMessage);
    setLocationError(errorMessage);
  };

  // Reverse geocode using Nominatim API
  const reverseGeocode = async (location) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}&addressdetails=1&accept-language=en`
      );
      const data = await response.json();
      
      if (data && data.display_name) {
        console.log('Reverse geocoded address:', data.display_name);
        setInputValue(data.display_name);
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };

  // Search for similar cities using Nominatim
  const searchCities = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search to avoid too many API calls
    searchTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=8&addressdetails=1&accept-language=en`
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          const citySuggestions = data.map((place, index) => ({
            id: place.place_id || `place_${index}`,
            description: place.display_name,
            lat: parseFloat(place.lat),
            lng: parseFloat(place.lon),
            structured_formatting: {
              main_text: place.name || place.display_name.split(',')[0],
              secondary_text: place.display_name.split(',').slice(1).join(',').trim()
            }
          }));
          setSuggestions(citySuggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        console.error('Error searching cities:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    console.log('Input changed:', value, 'Length:', value.length);
    
    setInputValue(value);
    
    // If input is cleared, automatically get current location
    if (!value.trim()) {
      
    } else {
      console.log('Manual input detected');
      setIsManualInput(true);
      // Search for similar cities
      searchCities(value);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    console.log("handleSuggestionSelect suggestion :", suggestion);
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    
    // Set location directly from suggestion data
    if (suggestion.lat && suggestion.lng) {
      const newLocation = { lat: suggestion.lat, lng: suggestion.lng };
      setLocation(newLocation);
      setLocationnew(newLocation);
      setIsManualInput(false);
      localStorage.setItem('location-lat', suggestion.lat);
      localStorage.setItem('location-lng', suggestion.lng);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // On mount, automatically get current location
  useEffect(() => {
    console.log("run in location search 4"); 
    if (!inputValue && !isManualInput) {
      getCurrentLocation();
    }
  }, []);

  // Removed redundant reverse geocoding - already handled in getCurrentLocation()

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        type="text"
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        placeholder="Enter location"
        className="form-control"
        style={{ width: '100%' }}
        disabled={isGettingLocation}
      />
      <button
        type="button"
        onClick={getCurrentLocation}
        disabled={isGettingLocation}
        style={{
          padding: '10px',
          position: 'absolute',
          right: '0px',
          width: '43px',
          height: '40px',
          borderBottomRightRadius: '4px',
          borderTopRightRadius: '4px',
          opacity: isGettingLocation ? 0.6 : 1,
          cursor: isGettingLocation ? 'not-allowed' : 'pointer'
        }}
      >
        {isGettingLocation ? (
          <div style={{ 
            width: '16px', 
            height: '16px', 
            border: '2px solid #3d4461', 
            borderTop: '2px solid transparent', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }} />
        ) : (
          <FaMapMarkerAlt style={{ color: '#3d4461' }} />
        )}
      </button>

      {/* City Suggestions Dropdown */}
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto'
          }}
        >
          {isLoading ? (
            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
              Searching cities...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                onClick={() => handleSuggestionSelect(suggestion)}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                <div style={{ fontWeight: '500', color: '#333' }}>
                  {suggestion.structured_formatting?.main_text || suggestion.description.split(',')[0]}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                  {suggestion.description}
                </div>
              </div>
            ))
          ) : (
            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
              No cities found
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {locationError && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          marginTop: '4px',
          border: '1px solid #f5c6cb',
          zIndex: 1001
        }}>
          {locationError}
        </div>
      )}

      {/* Hidden Inputs */}
      <input type="hidden" name="location_lat" value={locationnew.lat} />
      <input type="hidden" name="location_lng" value={locationnew.lng} />
      
      {/* CSS for loading animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default LocationSearch;
